/**
 * Backend-free “Beat My Edit” challenge payload.
 * Compact versioned JSON + UTF-8 base64url — no dependencies.
 * Never executes payload strings; score display metadata is recomputed via scoreEdit.
 */
import type { EditGraph } from "../contracts/edit-graph.js";
import type { ScoreBreakdown } from "../contracts/score.js";
import type { SourcePack } from "../contracts/source-pack.js";
import { validateEditGraph, validateEditGraphAgainstPack } from "../validation/validate.js";
import { scoreEdit } from "../scoring/score.js";

export const CHALLENGE_SCHEMA_VERSION = 1 as const;

/** Authored constraint ids for this slice (empty array = none). */
export const CHALLENGE_CONSTRAINT_IDS = [
  "keep-dialogue-context",
  "no-ominous-sound",
  "deadline-bonus",
] as const;

export type ChallengeConstraintId = (typeof CHALLENGE_CONSTRAINT_IDS)[number];

/** Max base64url characters in the query value (ordinary browser URL budget). */
export const CHALLENGE_MAX_ENCODED_CHARS = 12_000;
/** Max UTF-8 byte length of the decoded JSON string. */
export const CHALLENGE_MAX_DECODED_BYTES = 16_000;
/** Max EditGraph ops (cursor-bounded active + redo branch). */
export const CHALLENGE_MAX_OPS = 48;

export class ChallengePayloadError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "ChallengePayloadError";
    this.code = code;
  }
}

export interface ChallengeScoreSummary {
  reach: number;
  contextLoss: number;
  claimDrift: number;
  credDelta: number;
  provenanceRisk: number;
}

export interface ChallengeEncodeInput {
  sourcePackId: string;
  sourcePackVersion: string;
  seed: number;
  graph: EditGraph;
  constraintIds?: readonly ChallengeConstraintId[];
  /** Display-only; recomputed on decode. */
  scoreSummary?: ChallengeScoreSummary;
}

export interface DecodedChallenge {
  schemaVersion: typeof CHALLENGE_SCHEMA_VERSION;
  sourcePackId: string;
  sourcePackVersion: string;
  seed: number;
  graph: EditGraph;
  constraintIds: ChallengeConstraintId[];
  /** Always from scoreEdit — never trusted display metadata. */
  score: ScoreBreakdown;
  pack: SourcePack;
  displayScoreWasReplaced: boolean;
}

export interface ChallengeDecodeOptions {
  lookupPack: (id: string, version: string) => SourcePack | null;
}

/** Compact wire shape — short keys only. */
interface ChallengeWireV1 {
  v: typeof CHALLENGE_SCHEMA_VERSION;
  p: string;
  pv: string;
  s: number;
  g: unknown;
  c?: string[];
  m?: {
    r: number;
    cl: number;
    cd: number;
    cr: number;
    pr: number;
  };
}

const CONSTRAINT_SET = new Set<string>(CHALLENGE_CONSTRAINT_IDS);

export function encodeChallengePayload(input: ChallengeEncodeInput): string {
  assertGraphMatchesEnvelope(input);
  const constraints = normalizeConstraintIds(input.constraintIds ?? []);
  if (input.graph.ops.length > CHALLENGE_MAX_OPS) {
    throw new ChallengePayloadError("op_limit", `ops exceed ${CHALLENGE_MAX_OPS}`);
  }
  // Validate graph shape before packing (timing/tokens via shape validation).
  validateEditGraph(input.graph);

  const wire: ChallengeWireV1 = {
    v: CHALLENGE_SCHEMA_VERSION,
    p: input.sourcePackId,
    pv: input.sourcePackVersion,
    s: input.seed,
    g: input.graph,
  };
  if (constraints.length > 0) {
    wire.c = [...constraints];
  }
  if (input.scoreSummary) {
    assertScoreSummary(input.scoreSummary);
    wire.m = {
      r: input.scoreSummary.reach,
      cl: input.scoreSummary.contextLoss,
      cd: input.scoreSummary.claimDrift,
      cr: input.scoreSummary.credDelta,
      pr: input.scoreSummary.provenanceRisk,
    };
  }

  const json = JSON.stringify(wire);
  const bytes = utf8ByteLength(json);
  if (bytes > CHALLENGE_MAX_DECODED_BYTES) {
    throw new ChallengePayloadError("oversize", `decoded JSON exceeds ${CHALLENGE_MAX_DECODED_BYTES} bytes`);
  }
  const encoded = utf8ToBase64Url(json);
  if (encoded.length > CHALLENGE_MAX_ENCODED_CHARS) {
    throw new ChallengePayloadError(
      "oversize",
      `encoded payload exceeds ${CHALLENGE_MAX_ENCODED_CHARS} chars`,
    );
  }
  return encoded;
}

export function decodeChallengePayload(
  encoded: string,
  options: ChallengeDecodeOptions,
): DecodedChallenge {
  if (typeof encoded !== "string" || encoded.length === 0) {
    throw new ChallengePayloadError("malformed", "empty payload");
  }
  if (encoded.length > CHALLENGE_MAX_ENCODED_CHARS) {
    throw new ChallengePayloadError("oversize", "encoded payload too large");
  }
  if (!/^[A-Za-z0-9_-]+$/.test(encoded)) {
    throw new ChallengePayloadError("malformed", "invalid base64url alphabet");
  }

  let json: string;
  try {
    json = base64UrlToUtf8(encoded);
  } catch {
    throw new ChallengePayloadError("malformed", "malformed base64");
  }
  if (utf8ByteLength(json) > CHALLENGE_MAX_DECODED_BYTES) {
    throw new ChallengePayloadError("oversize", "decoded JSON too large");
  }

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new ChallengePayloadError("malformed", "malformed JSON");
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new ChallengePayloadError("malformed", "payload must be an object");
  }
  const obj = raw as Record<string, unknown>;
  if (obj.v !== CHALLENGE_SCHEMA_VERSION) {
    throw new ChallengePayloadError(
      "schema",
      `unknown schema version ${String(obj.v)}`,
    );
  }

  const sourcePackId = requireNonEmptyString("p", obj.p);
  const sourcePackVersion = requireNonEmptyString("pv", obj.pv);
  const seed = requireFiniteInt("s", obj.s);
  const constraints = normalizeConstraintIds(
    Array.isArray(obj.c) ? (obj.c as unknown[]) : [],
  );

  let displayMeta: ChallengeScoreSummary | null = null;
  if (obj.m !== undefined && obj.m !== null) {
    displayMeta = parseScoreMeta(obj.m);
  }

  let graph: EditGraph;
  try {
    graph = validateEditGraph(obj.g);
  } catch (err) {
    throw new ChallengePayloadError(
      "invalid_graph",
      err instanceof Error ? err.message : "invalid graph",
    );
  }
  if (graph.ops.length > CHALLENGE_MAX_OPS) {
    throw new ChallengePayloadError("op_limit", `ops exceed ${CHALLENGE_MAX_OPS}`);
  }
  if (graph.seed !== seed) {
    throw new ChallengePayloadError("mismatch", "graph seed does not match payload seed");
  }
  if (graph.sourcePackId !== sourcePackId) {
    throw new ChallengePayloadError("mismatch", "graph pack id does not match payload");
  }
  if (graph.sourcePackVersion !== sourcePackVersion) {
    throw new ChallengePayloadError("mismatch", "graph pack version does not match payload");
  }

  const pack = options.lookupPack(sourcePackId, sourcePackVersion);
  if (!pack) {
    throw new ChallengePayloadError("unknown_pack", "unknown source pack or version");
  }
  try {
    validateEditGraphAgainstPack(graph, pack);
  } catch (err) {
    throw new ChallengePayloadError(
      "invalid_graph",
      err instanceof Error ? err.message : "graph failed pack validation",
    );
  }

  const score = scoreEdit(pack, graph);
  let displayScoreWasReplaced = false;
  if (displayMeta) {
    if (
      displayMeta.reach !== score.reach ||
      displayMeta.contextLoss !== score.contextLoss ||
      displayMeta.claimDrift !== score.claimDrift ||
      displayMeta.credDelta !== score.credDelta ||
      displayMeta.provenanceRisk !== score.provenanceRisk
    ) {
      displayScoreWasReplaced = true;
    }
  }

  return {
    schemaVersion: CHALLENGE_SCHEMA_VERSION,
    sourcePackId,
    sourcePackVersion,
    seed,
    graph,
    constraintIds: constraints,
    score,
    pack,
    displayScoreWasReplaced,
  };
}

function assertGraphMatchesEnvelope(input: ChallengeEncodeInput): void {
  if (input.graph.seed !== input.seed) {
    throw new ChallengePayloadError("mismatch", "graph seed does not match envelope seed");
  }
  if (input.graph.sourcePackId !== input.sourcePackId) {
    throw new ChallengePayloadError("mismatch", "graph pack id does not match envelope");
  }
  if (input.graph.sourcePackVersion !== input.sourcePackVersion) {
    throw new ChallengePayloadError("mismatch", "graph pack version does not match envelope");
  }
}

function normalizeConstraintIds(raw: readonly unknown[]): ChallengeConstraintId[] {
  const out: ChallengeConstraintId[] = [];
  for (const id of raw) {
    if (typeof id !== "string" || !CONSTRAINT_SET.has(id)) {
      throw new ChallengePayloadError("constraint", `unknown constraint id ${String(id)}`);
    }
    if (!out.includes(id as ChallengeConstraintId)) {
      out.push(id as ChallengeConstraintId);
    }
  }
  return out;
}

function assertScoreSummary(s: ChallengeScoreSummary): void {
  for (const [k, v] of Object.entries(s)) {
    if (typeof v !== "number" || !Number.isFinite(v) || !Number.isInteger(v)) {
      throw new ChallengePayloadError("score", `invalid score field ${k}`);
    }
  }
}

function parseScoreMeta(raw: unknown): ChallengeScoreSummary {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new ChallengePayloadError("score", "score metadata must be an object");
  }
  const m = raw as Record<string, unknown>;
  const summary: ChallengeScoreSummary = {
    reach: requireScoreInt("m.r", m.r),
    contextLoss: requireScoreInt("m.cl", m.cl),
    claimDrift: requireScoreInt("m.cd", m.cd),
    credDelta: requireScoreInt("m.cr", m.cr),
    provenanceRisk: requireScoreInt("m.pr", m.pr),
  };
  return summary;
}

function requireNonEmptyString(path: string, v: unknown): string {
  if (typeof v !== "string" || v.length === 0) {
    throw new ChallengePayloadError("malformed", `${path} must be a non-empty string`);
  }
  return v;
}

function requireFiniteInt(path: string, v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v) || !Number.isInteger(v)) {
    throw new ChallengePayloadError("malformed", `${path} must be a finite integer`);
  }
  return v;
}

function requireScoreInt(path: string, v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v) || !Number.isInteger(v)) {
    throw new ChallengePayloadError("score", `${path} must be a finite integer`);
  }
  return v;
}

function utf8ByteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

function utf8ToBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  const b64 =
    typeof btoa === "function"
      ? btoa(binary)
      : Buffer.from(bytes).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToUtf8(encoded: string): string {
  const padded = encoded + "=".repeat((4 - (encoded.length % 4)) % 4);
  const b64 = padded.replace(/-/g, "+").replace(/_/g, "/");
  let binary: string;
  try {
    if (typeof atob === "function") {
      binary = atob(b64);
    } else {
      return Buffer.from(b64, "base64").toString("utf8");
    }
  } catch {
    throw new ChallengePayloadError("malformed", "malformed base64");
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}
